// src/components/ChecklistGrouped.js
import React, { useState, useMemo, useCallback } from "react";
import { Form, Button, ProgressBar, Badge } from "react-bootstrap";
import {
  groupChecklistBySection,
  getSectionLabel,
  getSectionProgress,
  hasChecklistSections,
  getAvailableSections
} from "../utils/checklistHelpers.js";

// --- Sub-componentes internos (DRY) ---

function ProgressHeader({ checklist, language }) {
  const progress = useMemo(() => getSectionProgress(checklist), [checklist]);
  if (progress.total === 0) return null;

  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <small className="text-muted fw-bold">
          {language === "en" ? "Progress" : "Progresso"}: {progress.completed}/{progress.total} ({progress.percentage}%)
        </small>
        {progress.isComplete && (
          <Badge bg="success">
            {language === "en" ? "Complete!" : "Completo!"}
          </Badge>
        )}
      </div>
      <ProgressBar
        now={progress.percentage}
        variant={progress.isComplete ? "success" : progress.percentage > 50 ? "info" : "primary"}
        style={{ height: "8px" }}
      />
    </div>
  );
}

function ChecklistItem({ item, onToggle, onRemove, isEditable, canManage }) {
  return (
    <div className="d-flex align-items-center gap-2 mb-1 ps-2">
      <Form.Check
        type="checkbox"
        checked={item.done}
        onChange={() => onToggle(item.originalIndex)}
        disabled={!isEditable}
      />
      <span
        style={{
          textDecoration: item.done ? "line-through" : "none",
          color: item.done ? "#6c757d" : "#212529",
          flex: 1,
          fontSize: "0.9rem"
        }}
      >
        {item.displayText}
      </span>
      {canManage && (
        <Button
          variant="link"
          size="sm"
          className="text-danger p-0"
          onClick={() => onRemove(item.originalIndex)}
        >
          ✕
        </Button>
      )}
    </div>
  );
}

function SectionGroup({ section, items, language, onToggle, onRemove, isEditable, canManage }) {
  const progress = useMemo(() => getSectionProgress(items), [items]);
  const [collapsed, setCollapsed] = useState(false);

  // Auto-colapso visual: secao completa mostra como colapsada por padrao
  // mas o usuario pode expandir clicando
  const isEffectivelyCollapsed = collapsed || (progress.isComplete && !collapsed._expanded);

  // Para o auto-colapso funcionar com toggle manual, usamos logica simples:
  // Se o usuario nunca clicou, secao completa = colapsada. Se clicou, respeita o clique.
  const [userToggled, setUserToggled] = useState(false);
  const showItems = userToggled ? !collapsed : !progress.isComplete;

  const handleToggle = useCallback(() => {
    setUserToggled(true);
    setCollapsed(prev => !prev);
  }, []);

  const label = getSectionLabel(section, language);

  return (
    <div
      className="mb-2 border rounded"
      style={{
        backgroundColor: progress.isComplete ? "#d4edda" : "#fff",
        transition: "background-color 0.3s"
      }}
    >
      {/* Header clicavel */}
      <div
        className="d-flex align-items-center justify-content-between px-3 py-2"
        style={{ cursor: "pointer", userSelect: "none" }}
        onClick={handleToggle}
      >
        <div className="d-flex align-items-center gap-2">
          <span style={{ fontSize: "0.75rem", width: "14px", color: "#212529" }}>
            {showItems ? "▼" : "▶"}
          </span>
          <strong style={{ fontSize: "0.9rem", color: "#212529" }}>{label}</strong>
        </div>
        <Badge
          bg={progress.isComplete ? "success" : "secondary"}
          pill
          style={{ fontSize: "0.75rem" }}
        >
          {progress.completed}/{progress.total}
        </Badge>
      </div>

      {/* Items */}
      {showItems && (
        <div className="pb-2 px-2">
          {items.map((item) => (
            <ChecklistItem
              key={item.originalIndex}
              item={item}
              onToggle={onToggle}
              onRemove={onRemove}
              isEditable={isEditable}
              canManage={canManage}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AddItemInput({ onAdd, language, hasSections }) {
  const [text, setText] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const sections = useMemo(() => getAvailableSections(language), [language]);

  const handleAdd = useCallback(() => {
    if (!text.trim()) return;
    // Se tem secoes E uma secao selecionada, formata com prefixo
    const formattedText = (hasSections && selectedSection)
      ? `[${selectedSection}] ${text.trim()}`
      : text.trim();
    onAdd(formattedText);
    setText("");
  }, [text, selectedSection, hasSections, onAdd]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  }, [handleAdd]);

  return (
    <div className="d-flex gap-2 mb-2">
      {hasSections && (
        <Form.Select
          size="sm"
          value={selectedSection}
          onChange={(e) => setSelectedSection(e.target.value)}
          style={{ width: "140px", flexShrink: 0 }}
        >
          <option value="">
            {language === "en" ? "No section" : "Sem seção"}
          </option>
          {sections.map(s => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </Form.Select>
      )}
      <Form.Control
        type="text"
        size="sm"
        placeholder={language === "en" ? "Add item..." : "Adicionar item..."}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyPress={handleKeyPress}
      />
      <Button onClick={handleAdd} variant="outline-primary" size="sm">+</Button>
    </div>
  );
}

// --- Componente principal ---

export default function ChecklistGrouped({
  checklist,
  onToggle,
  onRemove,
  onAdd,
  language,
  isEditable,
  canManage
}) {
  const hasSections = useMemo(() => hasChecklistSections(checklist), [checklist]);
  const grouped = useMemo(() => groupChecklistBySection(checklist), [checklist]);

  if (!checklist || checklist.length === 0) {
    // Sem itens - mostra so o input se pode gerenciar
    return canManage ? (
      <AddItemInput onAdd={onAdd} language={language} hasSections={false} />
    ) : (
      <small className="text-muted">
        {language === "en" ? "No checklist items" : "Nenhum item na checklist"}
      </small>
    );
  }

  // Lista plana (regular, moveout) - sem secoes
  if (!hasSections) {
    return (
      <>
        {canManage && (
          <AddItemInput onAdd={onAdd} language={language} hasSections={false} />
        )}
        <ProgressHeader checklist={checklist} language={language} />
        <div>
          {checklist.map((item, index) => (
            <div key={index} className="d-flex align-items-center gap-2 mb-2">
              <Form.Check
                type="checkbox"
                checked={item.done}
                onChange={() => onToggle(index)}
                disabled={!isEditable}
              />
              <span style={{ textDecoration: item.done ? "line-through" : "none", flex: 1 }}>
                {item.text}
              </span>
              {canManage && (
                <Button
                  variant="link"
                  size="sm"
                  className="text-danger p-0"
                  onClick={() => onRemove(index)}
                >
                  ✕
                </Button>
              )}
            </div>
          ))}
        </div>
      </>
    );
  }

  // Lista agrupada (airbnb, postconstruction)
  return (
    <>
      {canManage && (
        <AddItemInput onAdd={onAdd} language={language} hasSections={true} />
      )}
      <ProgressHeader checklist={checklist} language={language} />
      {grouped.map(group => (
        <SectionGroup
          key={group.section}
          section={group.section}
          items={group.items}
          language={language}
          onToggle={onToggle}
          onRemove={onRemove}
          isEditable={isEditable}
          canManage={canManage}
        />
      ))}
    </>
  );
}
